import { BILLING_INTERVALS, GRANT_DAYS, ordinal } from '@/config/membershipTierWizardOptions'
import { formatCents } from '@/helpers/memberPricing'

const IMMEDIATE = 'ImmediateMembershipPassReward'
const CREDIT = 'AccountCreditMembershipPassReward'
const DELIVERY = 'FulfillmentMembershipPassReward'
const BILLING_VALUES = BILLING_INTERVALS.map(interval => interval.value)
const DAY_VALUES = GRANT_DAYS.map(day => day.value)
const EXPIRY_TYPES = ['day', 'week', 'month', 'year']
const LAST_DATE = 31

export const REWARD_TYPES = [
  {
    value: IMMEDIATE,
    title: 'A discount when they add the pass',
    subtitle: 'An amount off the order where a customer adds the pass.'
  },
  {
    value: CREDIT,
    title: 'Account credit on a schedule',
    subtitle: "Credit added to the customer's account every week, every 2 weeks or every month."
  },
  {
    value: DELIVERY,
    title: 'Free delivery',
    subtitle: 'Free delivery or shipping on every order while the customer holds the pass.'
  }
]

export const CREDIT_REWARD = CREDIT

const blank = value => value === null || value === undefined

const toKey = value => (blank(value) ? null : String(value))

const toId = value => (blank(value) ? null : Number(value))

export const newReward = type => ({
  id: null,
  type,
  amountCents: null,
  interval: type === CREDIT ? 'weekly' : null,
  awardDay: type === CREDIT ? 'monday' : null,
  awardDate: null,
  autoApply: false,
  expiryType: null,
  expiryValue: null
})

const wholeNumber = value => Number.isInteger(value) && value >= 1

const amountValid = reward => Number.isInteger(reward.amountCents) && reward.amountCents > 0

const scheduleValid = ({ interval, awardDay, awardDate }) =>
  interval === 'monthly'
    ? Number.isInteger(awardDate) && awardDate >= 1 && awardDate <= LAST_DATE
    : BILLING_VALUES.includes(interval) && DAY_VALUES.includes(awardDay)

const expiryValid = ({ expiryType, expiryValue }) =>
  (blank(expiryType) && blank(expiryValue)) || (EXPIRY_TYPES.includes(expiryType) && wholeNumber(expiryValue))

const REWARD_RULES = {
  [IMMEDIATE]: amountValid,
  [CREDIT]: reward => amountValid(reward) && scheduleValid(reward) && expiryValid(reward),
  [DELIVERY]: () => true
}

export const rewardValid = reward => (REWARD_RULES[reward.type] ? REWARD_RULES[reward.type](reward) : true)

export const passValid = pass =>
  !pass ||
  !pass.linked ||
  (pass.name.trim().length > 0 &&
    Number.isInteger(pass.priceCents) &&
    pass.priceCents >= 0 &&
    BILLING_VALUES.includes(pass.billingInterval) &&
    pass.rewards.every(rewardValid))

const dayName = day => (day ? day.charAt(0).toUpperCase() + day.slice(1) : '')

const creditSchedule = ({ interval, awardDay, awardDate }) => {
  if (interval === 'monthly') return `on the ${ordinal(awardDate)} of each month`
  if (interval === 'every_two_weeks') return `every other ${dayName(awardDay)}`
  return `every ${dayName(awardDay)}`
}

const creditExpiry = ({ expiryType, expiryValue }) =>
  blank(expiryType) ? '' : `, expiring after ${expiryValue} ${expiryValue === 1 ? expiryType : `${expiryType}s`}`

const REWARD_LABELS = {
  [IMMEDIATE]: (reward, currency) => `${formatCents(reward.amountCents ?? 0, currency)} off when they add the pass`,
  [CREDIT]: (reward, currency) =>
    `${formatCents(reward.amountCents ?? 0, currency)} account credit ${creditSchedule(reward)}${
      reward.autoApply ? ', applied at checkout automatically' : ''
    }${creditExpiry(reward)}`,
  [DELIVERY]: () => 'Free delivery or shipping on every order'
}

export const rewardLabel = (reward, currency = 'USD') =>
  REWARD_LABELS[reward.type] ? REWARD_LABELS[reward.type](reward, currency) : 'Another kind of reward, kept as saved'

const SECTIONS = {
  passLink: ({ name, customers, passName }) => ({
    title: 'Pass',
    description: `${passName} is a separate subscription ${customers} can add at checkout, with rewards while they hold the pass. Switch the pass off to unlink ${passName} from ${name}. The pass itself is kept.`
  }),
  passDetails: ({ passName }) => ({
    title: 'Pass details',
    description: `How ${passName} is named and described to customers at checkout.`
  }),
  passBilling: ({ passName }) => ({
    title: 'Price and billing',
    description: `What ${passName} costs, how often customers are billed, and whether checkout picks ${passName} for them.`
  }),
  passRewards: ({ passName }) => ({
    title: 'Rewards',
    description: `What customers get while they hold ${passName}.`
  })
}

export const passSectionCopy = (key, words) => SECTIONS[key](words)

const rewardFromState = reward => ({
  id: toKey(reward.id),
  type: reward.type,
  amountCents: reward.credit_amount_cents,
  interval: reward.credit_interval,
  awardDay: reward.credit_award_day,
  awardDate: reward.credit_award_date,
  autoApply: Boolean(reward.auto_apply),
  expiryType: reward.expiry_type,
  expiryValue: reward.expiry_value
})

export const passFromState = pass =>
  pass
    ? {
        linked: Boolean(pass.linked),
        name: pass.name ?? '',
        description: pass.description ?? '',
        priceCents: pass.price_cents,
        billingInterval: pass.billing_interval,
        autoSelect: Boolean(pass.auto_select),
        rewards: (pass.rewards ?? []).map(rewardFromState)
      }
    : null

const orNull = value => (blank(value) ? null : value)

const rewardPayload = reward => ({
  id: toId(reward.id),
  type: reward.type,
  credit_amount_cents: orNull(reward.amountCents),
  credit_interval: orNull(reward.interval),
  credit_award_day: orNull(reward.awardDay),
  credit_award_date: orNull(reward.awardDate),
  auto_apply: Boolean(reward.autoApply),
  expiry_type: orNull(reward.expiryType),
  expiry_value: orNull(reward.expiryValue)
})

export const passPayload = pass =>
  pass
    ? {
        linked: pass.linked,
        name: pass.name,
        description: pass.description,
        price_cents: orNull(pass.priceCents),
        billing_interval: pass.billingInterval,
        auto_select: pass.autoSelect,
        rewards: pass.rewards.map(rewardPayload)
      }
    : null
